// SPDX-License-Identifier: AGPL-3.0-or-later

import {PRODUCT_NAME} from '@app/features/app/config/I18nDisplayConstants';
import {Select as FormSelect} from '@app/features/ui/components/form/FormSelect';
import styles from '@app/features/user/components/modals/tabs/notifications_tab/PushSettings.module.css';
import {msg} from '@lingui/core/macro';
import {useLingui} from '@lingui/react/macro';
import {observer} from 'mobx-react-lite';
import type React from 'react';
import {useMemo} from 'react';

const PUSH_NOTIFICATION_INACTIVE_TIMEOUT_DESCRIPTOR = msg({
	message: 'Push notification inactive timeout',
	comment: 'Label in the push settings.',
});
const AVOIDS_SENDING_PUSH_NOTIFICATIONS_TO_YOUR_MOBILE_DEVICES_DESCRIPTOR = msg({
	message:
		'{productName} avoids sending push notifications to your mobile devices when you are at your computer. Choose how long you need to be inactive on desktop before you receive push notifications.',
	comment: 'Label in the push settings.',
});
const ONE_MINUTE_DESCRIPTOR = msg({
	message: '{oneMinute} minute',
	comment: 'Push notification inactive timeout option. The value is a duration in minutes.',
});
const MINUTES_DESCRIPTOR = msg({
	message: '{minutes} minutes',
	comment: 'Push notification inactive timeout option. The value is a duration in minutes.',
});

interface PushSettingsProps {
	afkTimeout: number;
	onAfkTimeoutChange: (value: number) => void;
	showHeader?: boolean;
}

export const PushSettings: React.FC<PushSettingsProps> = observer(
	({afkTimeout, onAfkTimeoutChange, showHeader = true}) => {
		const {i18n} = useLingui();
		const timeoutOptions = useMemo<Array<{value: number; label: string}>>(() => {
			const oneMinute = 1;
			return Array.from({length: 10}, (_, index) => {
				const minutes = index + 1;
				return {
					value: minutes,
					label:
						minutes === oneMinute ? i18n._(ONE_MINUTE_DESCRIPTOR, {oneMinute}) : i18n._(MINUTES_DESCRIPTOR, {minutes}),
				};
			});
		}, [i18n]);
		const timeoutMinutes = Math.max(1, Math.min(10, Math.round(afkTimeout / 60)));
		return (
			<div className={styles.container} data-flx="user.notifications-tab.push-settings.container">
				{showHeader && (
					<>
						<p className={styles.title} data-flx="user.notifications-tab.push-settings.title">
							{i18n._(PUSH_NOTIFICATION_INACTIVE_TIMEOUT_DESCRIPTOR)}
						</p>
						<p className={styles.description} data-flx="user.notifications-tab.push-settings.description">
							{i18n._(AVOIDS_SENDING_PUSH_NOTIFICATIONS_TO_YOUR_MOBILE_DEVICES_DESCRIPTOR, {productName: PRODUCT_NAME})}
						</p>
					</>
				)}
				<FormSelect<number>
					value={timeoutMinutes}
					options={timeoutOptions}
					onChange={onAfkTimeoutChange}
					data-flx="user.notifications-tab.push-settings.form-select"
				/>
			</div>
		);
	},
);
